import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import {
  useGetBusinessQuery,
  useGetBusinessStoreQuery,
  useCreateBusinessStoreMutation,
  useUpdateBusinessStoreMutation,
} from "../../store/api";
import { getStoreProblemInfo } from "../../utils/problemDetails";

const defaultForm = {
  restaurantId: "",
  storeCode: "",
  storeSlug: "",
  storeName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  countryCode: "",
  qrBaseUrl: "",
  edgePublicBaseUrl: "",
  status: "ACTIVE",
};

const mapStoreToForm = (store) => ({
  restaurantId: store?.restaurantId || "",
  storeCode: store?.storeCode || store?.code || "",
  storeSlug: store?.storeSlug || store?.slug || "",
  storeName: store?.storeName || store?.name || "",
  addressLine1: store?.addressLine1 || store?.address?.addressLine1 || "",
  addressLine2: store?.addressLine2 || store?.address?.addressLine2 || "",
  city: store?.city || store?.address?.city || "",
  state: store?.state || store?.address?.state || "",
  postalCode: store?.postalCode || store?.address?.postalCode || "",
  countryCode: store?.countryCode || store?.address?.countryCode || "",
  qrBaseUrl: store?.qrBaseUrl || store?.qr?.baseUrl || "",
  edgePublicBaseUrl: store?.edgePublicBaseUrl || "",
  status: (
    store?.status ||
    (store?.isActive === true ? "ACTIVE" : store?.isActive === false ? "INACTIVE" : "ACTIVE")
  ).toUpperCase(),
});

const StoreForm = () => {
  const { businessId, storeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(storeId);
  const createBusinessContext = !isEdit ? location.state?.createBusinessContext : null;
  const { data: business } = useGetBusinessQuery(businessId);
  const {
    data: storeData,
    isLoading: isStoreLoading,
    isError: isStoreError,
    error: storeError,
  } = useGetBusinessStoreQuery(
    { businessId, storeId },
    { skip: !isEdit || !businessId || !storeId },
  );
  const [createBusinessStore, createState] = useCreateBusinessStoreMutation();
  const [updateBusinessStore, updateState] = useUpdateBusinessStoreMutation();

  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [maxStoresMessage, setMaxStoresMessage] = useState("");

  const isSubmitting = createState.isLoading || updateState.isLoading;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (
      !isEdit &&
      createBusinessContext?.businessId &&
      createBusinessContext?.formSnapshot
    ) {
      navigate("/businesses/new", {
        replace: true,
        state: {
          restoreCreatedBusiness: createBusinessContext,
        },
      });
      return;
    }
    navigate(-1);
  };

  useEffect(() => {
    if (isEdit && storeData) {
      setForm(mapStoreToForm(storeData));
      setErrors({});
    }
  }, [isEdit, storeData]);

  const pageTitle = useMemo(
    () => (isEdit ? "Edit Store" : "Create Store"),
    [isEdit],
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.storeCode.trim()) {
      nextErrors.storeCode = "Store code is required.";
    }
    if (!form.storeSlug.trim()) {
      nextErrors.storeSlug = "Store slug is required.";
    }
    if (!form.storeName.trim()) {
      nextErrors.storeName = "Store name is required.";
    }
    if (!form.status.trim()) {
      nextErrors.status = "Status is required.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => ({
    restaurantId: form.restaurantId.trim() || null,
    storeCode: form.storeCode.trim(),
    storeSlug: form.storeSlug.trim().toLowerCase(),
    storeName: form.storeName.trim(),
    addressLine1: form.addressLine1.trim() || null,
    addressLine2: form.addressLine2.trim() || null,
    city: form.city.trim() || null,
    state: form.state.trim() || null,
    postalCode: form.postalCode.trim() || null,
    countryCode: form.countryCode.trim() || null,
    qrBaseUrl: form.qrBaseUrl.trim() || null,
    edgePublicBaseUrl: form.edgePublicBaseUrl.trim() || null,
    status: form.status.trim().toUpperCase(),
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    setMaxStoresMessage("");
    if (!validate()) return;

    const payload = buildPayload();

    try {
      if (isEdit) {
        await updateBusinessStore({
          businessId,
          storeId,
          payload,
        }).unwrap();
        navigate(`/businesses/${businessId}/stores/${storeId}`);
        return;
      }

      const result = await createBusinessStore({ businessId, payload }).unwrap();
      const createdStoreId = result?.storeId || result?.id;
      if (createdStoreId) {
        navigate(`/businesses/${businessId}/stores/${createdStoreId}`);
      } else {
        navigate(`/businesses/${businessId}/stores`);
      }
    } catch (err) {
      const problem = getStoreProblemInfo(err, {
        operation: isEdit ? "save" : "create",
      });
      if (Object.keys(problem.fieldErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...problem.fieldErrors }));
      }
      if (problem.showLicenseBanner) {
        setMaxStoresMessage(problem.message);
      } else {
        setFormError(problem.message);
      }
    }
  };

  if (isEdit && isStoreLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Edit Store"
          subtitle="Loading store details..."
          actions={
            <Button
              size="sm"
              variant="secondary"
              onClick={handleBack}
            >
              Back
            </Button>
          }
        />
        <Card>
          <p className="text-sm text-slate-500">Loading store details...</p>
        </Card>
      </div>
    );
  }

  if (isEdit && isStoreError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Edit Store"
          subtitle="Error loading store"
          actions={
            <Button
              size="sm"
              variant="secondary"
              onClick={handleBack}
            >
              Back
            </Button>
          }
        />
        <Card>
          <Alert tone="error">
            {getStoreProblemInfo(storeError, { operation: "load" }).message}
          </Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={pageTitle}
        subtitle={`Manage store details for ${business?.businessName || "this business"}.`}
        actions={
          <div className="flex gap-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleBack}
            >
              Back
            </Button>
            {isEdit && (
              <>
                <Link to={`/businesses/${businessId}/stores/${storeId}/features`}>
                  <Button size="sm" variant="secondary">
                    Add Feature
                  </Button>
                </Link>
                <Link to={`/businesses/${businessId}/stores/${storeId}`}>
                  <Button size="sm" variant="secondary">
                    View Store
                  </Button>
                </Link>
              </>
            )}
          </div>
        }
      />
      <Card>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="grid gap-4 md:grid-cols-3">
            <Input
              label="Store Code"
              name="storeCode"
              placeholder="NYC-001"
              value={form.storeCode}
              onChange={handleChange}
              error={errors.storeCode}
              required
            />
            <Input
              label="Store Slug"
              name="storeSlug"
              placeholder="nyc-main"
              value={form.storeSlug}
              onChange={handleChange}
              error={errors.storeSlug}
              helper="Lowercase URL-safe value."
              required
            />
            <Input
              label="Store Name"
              name="storeName"
              placeholder="New York Main"
              value={form.storeName}
              onChange={handleChange}
              error={errors.storeName}
              required
            />
            <Select
              label="Status"
              name="status"
              value={form.status}
              onChange={handleChange}
              error={errors.status}
              required
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </Select>
            <Input
              label="Restaurant ID"
              name="restaurantId"
              placeholder="Optional restaurant ID"
              value={form.restaurantId}
              onChange={handleChange}
            />
            <Input
              label="QR Base URL"
              name="qrBaseUrl"
              placeholder="https://qr.example.com/store"
              value={form.qrBaseUrl}
              onChange={handleChange}
            />
            <Input
              label="Edge Public Base URL"
              name="edgePublicBaseUrl"
              placeholder="https://store-a-edge.belinnov.co.in"
              value={form.edgePublicBaseUrl}
              onChange={handleChange}
            />
            <Input
              label="Address Line 1"
              name="addressLine1"
              placeholder="123 Main St"
              value={form.addressLine1}
              onChange={handleChange}
            />
            <Input
              label="Address Line 2"
              name="addressLine2"
              placeholder="Suite 4B"
              value={form.addressLine2}
              onChange={handleChange}
            />
            <Input
              label="City"
              name="city"
              placeholder="New York"
              value={form.city}
              onChange={handleChange}
            />
            <Input
              label="State"
              name="state"
              placeholder="NY"
              value={form.state}
              onChange={handleChange}
            />
            <Input
              label="Postal Code"
              name="postalCode"
              placeholder="10001"
              value={form.postalCode}
              onChange={handleChange}
            />
            <Input
              label="Country Code"
              name="countryCode"
              placeholder="US"
              value={form.countryCode}
              onChange={handleChange}
            />
          </div>

          {maxStoresMessage && (
            <Alert tone="warning">
              <p>{maxStoresMessage}</p>
              <Link
                to={`/businesses/${businessId}/licenses`}
                className="mt-2 inline-block font-semibold underline"
              >
                View license information
              </Link>
            </Alert>
          )}
          {formError && (
            <Alert tone="error">{formError}</Alert>
          )}

          <div className="flex flex-col gap-3 md:flex-row">
            <div className="md:basis-[70%]">
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting
                  ? "Saving..."
                  : isEdit
                    ? "Update Store"
                    : "Create Store"}
              </Button>
            </div>
            <div className="md:basis-[30%]">
              <Link to={`/businesses/${businessId}/stores/${storeId || "new"}/features`}>
                <Button type="button" className="w-full" variant="secondary">
                  Add Feature
                </Button>
              </Link>
            </div>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default StoreForm;
